/*
 * IMPORTS
 */
import React from "react"; // Npm: react.js library.
import Moment from "moment"; // Npm: Moment.js library for date and time formatting.
import _ from "underscore"; // Npm: Underscore.js library for utility functions.
import { IoMdAdd } from "react-icons/io"; // Npm: React icons.
import { HiArrowPath } from "react-icons/hi2"; // Npm: React icons.
import { useQuery } from "@apollo/client"; // Npm: Apollo client for GraphQL queries and mutations.
import { DataGrid } from "@mui/x-data-grid"; // Npm: Material UI Data Grid.
import {
  Button,
  Flex,
  Tab,
  TabList,
  Tabs,
  Tooltip,
  useBreakpointValue,
  useDisclosure,
} from "@chakra-ui/react"; // Npm: Chakra UI components.

/*
 * PACKAGES
 */
import DownloadToExcel from "components/DownloadToExcel";
import TableSpinner from "components/TableSpinner";
import Modal from "components/Modal";
import RateUpsert from "components/RateUpsert";
import RateDelete from "components/RateDelete";
import RateDropDownOptions from "components/RateDropDownOptions";

/*
 * GRAPHS
 */
import RateReadQuery from "./__query__/index.rate.read.query";

/*
 * STYLES
 */
import { buttonStyle, tabStyles } from "./index.style";

/*
 * OBJECTS
 */
const Index = () => {
  // Const assignment.
  const _rates = React.useMemo(
    () => Object.React.App.enums.CUSTOMER_TYPE.enums.map((i) => i.key),
    [],
  );
  const _successFlags = React.useMemo(
    () =>
      Object.React.App.enums.GRAPHQL_SUCCESSFUL_QUERY_FLAGS.enums.map(
        (i) => i.key,
      ),
    [],
  );

  // Hook assignment.
  const [paginationModel, setPaginationModel] = React.useState({
    pageSize: 10,
    page: 0,
  });
  const [columnVisibilityModel, setColumnVisibilityModel] = React.useState({
    id: false,
  });
  const [filter, setFilter] = React.useState({ items: [] });
  const _setFilterDebounced = React.useMemo(
    () => _.debounce(setFilter, 500),
    [],
  );
  const [tabIndex, setTabIndex] = React.useState(0);
  const [isRefreshing, setIsRefreshing] = React.useState(false);
  const _isPreviousPageRef = React.useRef(false);
  const _paginationModelRef = React.useRef(paginationModel);
  const _rowCountRef = React.useRef(0);

  React.useEffect(() => {
    return () => _setFilterDebounced.cancel?.();
  }, [_setFilterDebounced]);

  const {
    isOpen: isRateCreateFormOpen,
    onOpen: onRateCreateFormOpen,
    onClose: onRateCreateFormClose,
  } = useDisclosure();
  const {
    isOpen: isRateUpdateFormOpen,
    onOpen: onRateUpdateFormOpen,
    onClose: onRateUpdateFormClose,
  } = useDisclosure();
  const {
    onOpen: onDeleteModalOpen,
    isOpen: isDeleteModalOpen,
    onClose: onDeleteModalClose,
  } = useDisclosure();

  // Data assignment.
  const _isFilterApplied = filter?.items?.some(
    (i) => i.value != void 0 && i.value !== "",
  );
  const _appliedFilter = _isFilterApplied ? filter : void 0;

  const _QueryRateReadVariables = React.useMemo(
    () => ({
      type: _rates[tabIndex],
      filter: _appliedFilter,
      skip: paginationModel.page * paginationModel.pageSize,
      take: paginationModel.pageSize,
    }),
    [
      _appliedFilter,
      _rates,
      paginationModel.page,
      paginationModel.pageSize,
      tabIndex,
    ],
  );

  const _QueryRateRead = useQuery(RateReadQuery, {
    variables: _QueryRateReadVariables,
    fetchPolicy: _isPreviousPageRef.current
      ? "cache-first"
      : Object.React.App.fetchPolicy,
  });

  const _HandlePaginationModelChange = React.useCallback(
    (__paginationModel) => {
      // Only backward pagination with the same page size should prefer cached rows.
      _isPreviousPageRef.current =
        __paginationModel.page < _paginationModelRef.current.page &&
        __paginationModel.pageSize === _paginationModelRef.current.pageSize;

      _paginationModelRef.current = __paginationModel;
      setPaginationModel(__paginationModel);
    },
    [],
  );
  const _ResetPagination = React.useCallback(() => {
    _isPreviousPageRef.current = false;
    setPaginationModel((current) => {
      const nextPaginationModel = {
        ...current,
        page: 0,
      };

      _paginationModelRef.current = nextPaginationModel;

      return nextPaginationModel;
    });
  }, []);
  const _HandleFilterModelChange = React.useCallback(
    (__filterModel) => {
      _ResetPagination();
      _setFilterDebounced(__filterModel);
    },
    [_ResetPagination, _setFilterDebounced],
  );
  const _HandleTabChange = React.useCallback(
    (r) => {
      _ResetPagination();
      setTabIndex(r);
    },
    [_ResetPagination],
  );

  const _QueryRateReadRowCount = _.pluck(
    _QueryRateRead?.data?.RateRead,
    "_totalCount",
  )?.[0];
  const rowCount = React.useMemo(() => {
    if (undefined !== _QueryRateReadRowCount && null !== _QueryRateReadRowCount)
      _rowCountRef.current = _QueryRateReadRowCount;

    return _rowCountRef.current;
  }, [_QueryRateReadRowCount]);

  const _tableHeaderHeightRef = React.useRef(0);
  const _isCurrentViewMobile =
    useBreakpointValue({ base: true, md: false }, { fallback: "md" }) ?? false;

  // Event handler.
  const _HandleRefresh = React.useCallback(async () => {
    setIsRefreshing(true);

    try {
      await _QueryRateRead.refetch();
    } finally {
      setIsRefreshing(false);
    }
  }, [_QueryRateRead]);

  // Define columns
  const columns = React.useMemo(
    () => [
      {
        field: "sno",
        headerName: "S No.",
        align: "center",
        headerAlign: "center",
        width: 80,
        sortable: false,
        filterable: true,
        disableColumnMenu: true,
      },
      {
        field: "id",
        headerName: "ID",
        align: "center",
        editable: false,
        sortable: false,
        filterable: true,
        disableColumnMenu: true,
        headerAlign: "center",
      },
      {
        field: "displayName",
        headerName: "Rate Name",
        description: "Name of the rate.",
        editable: false,
        sortable: true,
        filterable: true,
        align: "center",
        headerAlign: "center",
        flex: 1,
        minWidth: 150,
      },
      {
        field: "type",
        headerName: "Rate Type",
        description: "Type of rate.",
        editable: false,
        sortable: true,
        filterable: true,
        disableColumnMenu: true,
        align: "center",
        headerAlign: "center",
        flex: 1,
        minWidth: 130,
      },
      {
        field: "createdAt",
        headerName: "CreatedAt",
        description: "Creation date of rate.",
        editable: false,
        sortable: true,
        filterable: true,
        align: "center",
        disableColumnMenu: true,
        headerAlign: "center",
        flex: 1,
        minWidth: 120,
      },
      {
        field: "updatedAt",
        headerName: "UpdatedAt",
        description: "Last update date of rate.",
        editable: false,
        sortable: true,
        filterable: true,
        align: "center",
        disableColumnMenu: true,
        headerAlign: "center",
        flex: 1,
        minWidth: 120,
      },
      {
        field: "createdBy",
        headerName: "Created By",
        description: "User who created the rate.",
        editable: false,
        sortable: false,
        filterable: false,
        align: "center",
        headerAlign: "center",
        disableColumnMenu: true,
        flex: 1,
        minWidth: 150,
      },
      {
        field: "action",
        headerName: "Action",
        description: "Actions available for the rate.",
        editable: false,
        sortable: false,
        filterable: false,
        align: "center",
        headerAlign: "center",
        disableColumnMenu: true,
        width: 100,
        renderCell: ({ row }) => (
          <RateDropDownOptions
            type={row?.type}
            rateId={row?.id}
            onRateDelete={onDeleteModalOpen}
            onRateUpdate={onRateUpdateFormOpen}
          />
        ),
      },
    ],
    [],
  );

  // Define rows
  const rows = React.useMemo(
    () =>
      _.compact(
        _QueryRateRead?.data?.RateRead?.map((r, index) =>
          _successFlags.includes(r.status)
            ? {
                ...r,
                id: r?.id ?? `rate-${paginationModel.page}-${paginationModel.pageSize}-${index}`,
                sno: `${index + paginationModel.page * paginationModel.pageSize + 1}.`,
                displayName: r?.displayName || "-",
                type: r?.type || "-",
                createdAt: r?.createdAt
                  ? Moment(r.createdAt).format("YYYY-MM-DD")
                  : "-",
                updatedAt: r?.updatedAt
                  ? Moment(r.updatedAt).format("YYYY-MM-DD")
                  : "-",
                createdBy: r?.Account?.displayName || "-",
              }
            : null,
        ),
      ),
    [
      _QueryRateRead?.data?.RateRead,
      paginationModel.page,
      paginationModel.pageSize,
    ],
  );

  // Return component.
  return (
    <>
      <Flex className="rate base" height="auto" flexDirection="column">
        <Flex display="flex" flex={1} flexDirection="column" height="100%">
          <Flex
            display="flex"
            flexDirection="row"
            justifyContent={
              _isCurrentViewMobile ? "flex-start" : "space-between"
            }
            alignItems="center"
            gap={_isCurrentViewMobile ? "10px" : "12px"}
            flexWrap={_isCurrentViewMobile ? "nowrap" : "wrap"}
            overflowX={_isCurrentViewMobile ? "auto" : "visible"}
            bg="linear-Gradient(73deg,#390EF6 0%, #9244F4 100%)"
            borderTopLeftRadius="20px"
            borderTopRadius="20px"
            p={_isCurrentViewMobile ? "12px" : "22px"}
          >
            <Tooltip label="Create new rate." fontSize="sm">
              <Button
                w={_isCurrentViewMobile ? "108px" : "170px"}
                minW={_isCurrentViewMobile ? "108px" : "170px"}
                onClick={onRateCreateFormOpen}
                leftIcon={<IoMdAdd />}
                style={{
                  ...buttonStyle,
                  fontSize: _isCurrentViewMobile
                    ? "14px"
                    : buttonStyle.fontSize,
                  lineHeight: _isCurrentViewMobile
                    ? "24px"
                    : buttonStyle.lineHeight,
                }}
              >
                New Rate
              </Button>
            </Tooltip>
            <Flex gap={_isCurrentViewMobile ? "10px" : "12px"} flexShrink={0}>
              <Button
                borderRadius="8px"
                fontWeight="450"
                minW={_isCurrentViewMobile ? "96px" : "20px"}
                px={_isCurrentViewMobile ? "15px" : "16px"}
                fontSize={_isCurrentViewMobile ? "14px" : "16px"}
                backgroundColor="purple.200"
                color="purple"
                _hover={{ bg: "purple.800", color: "white" }}
                leftIcon={<HiArrowPath />}
                isDisabled={isRefreshing}
                onClick={_HandleRefresh}
              >
                Refresh
              </Button>
              <DownloadToExcel
                onlyShowIcon={_isCurrentViewMobile}
                minW={_isCurrentViewMobile ? "40px" : void 0}
                w={_isCurrentViewMobile ? "40px" : void 0}
                px={_isCurrentViewMobile ? "0" : void 0}
                cellsData={
                  0 === _QueryRateRead?.data?.RateRead?.length ||
                  !_.every(
                    _.pluck(_QueryRateRead?.data?.RateRead, "status"),
                    (i) => _successFlags?.includes(i),
                  )
                    ? []
                    : _QueryRateRead?.data?.RateRead?.map((item, __index) => ({
                        "S.No.": `${__index + paginationModel.pageSize * paginationModel.page + 1}.`,
                        "Rate Name": item?.displayName ?? "-",
                        "Rate Type": item?.type ?? "-",
                        CreatedAt: Moment(item.createdAt).format("YYYY-MM-DD"),
                        UpdatedAt: Moment(item.updatedAt).format("YYYY-MM-DD"),
                        "Created By": item?.Account?.displayName ?? "-",
                      }))
                }
                headersData={[
                  "S.No.",
                  "Rate Name",
                  "Rate Type",
                  "CreatedAt",
                  "UpdatedAt",
                  "Created By",
                ].map((i) => ({ key: i, label: i }))}
              />
            </Flex>
          </Flex>
          <Tabs
            variant="enclosed"
            onChange={_HandleTabChange}
            display="flex"
            flexDirection="column"
            align="center"
            w="100%"
            flex={1}
            isFitted={!_isCurrentViewMobile}
          >
            <TabList
              bg="linear-Gradient(73deg,#390EF6 0%, rgba(146, 68, 244, 1) 100%)"
              overflowX={_isCurrentViewMobile ? "auto" : "none"}
              overflowY="hidden"
              w="100%"
              justifyContent={_isCurrentViewMobile ? "flex-start" : "center"}
              px={_isCurrentViewMobile ? "8px" : 0}
            >
              {_rates.map((u) => (
                <Tab
                  color="white"
                  textDecoration={"line-through"}
                  _focus={{ boxShadow: "none" }}
                  _selected={{ textDecoration: "none" }}
                  style={{
                    ...tabStyles,
                    border: "none",
                    borderRadius: 0,
                    fontSize: _isCurrentViewMobile
                      ? "13px"
                      : tabStyles.fontSize,
                    letterSpacing: 0,
                  }}
                  flex={_isCurrentViewMobile ? "0 0 auto" : 1}
                  minW={_isCurrentViewMobile ? "max-content" : void 0}
                  whiteSpace="nowrap"
                  px={_isCurrentViewMobile ? "14px" : "1rem"}
                  py="1rem"
                  key={u}
                >
                  {u}
                </Tab>
              ))}
            </TabList>
            <Flex
              height="calc(100vh - 300px)"
              minHeight="calc(100vh - 300px)"
              maxHeight="calc(100vh - 300px)"
              bg="white"
              flexDirection="column"
              border="2px solid #3A0FF6"
              borderTop="none"
              borderBottomRadius="15px"
              overflow="hidden"
            >
              {/* MUI DataGrid */}
              <DataGrid
                rows={rows}
                columns={columns}
                rowHeight={48}
                rowCount={rowCount}
                paginationMode="server"
                filterMode="server"
                paginationModel={paginationModel}
                onPaginationModelChange={_HandlePaginationModelChange}
                onFilterModelChange={_HandleFilterModelChange}
                pageSizeOptions={[10, 30, 50, 100]}
                slots={{
                  loadingOverlay: () => (
                    <TableSpinner
                      isLoading={true}
                      isEmpty={false}
                      chopHeightFromHundredPercentage={
                        _tableHeaderHeightRef?.current?.clientHeight
                      }
                    />
                  ),
                  noResultsOverlay: () => (
                    <TableSpinner
                      isLoading={false}
                      isEmpty={true}
                      chopHeightFromHundredPercentage={
                        _tableHeaderHeightRef?.current?.clientHeight
                      }
                    />
                  ),
                  noRowsOverlay: () => (
                    <TableSpinner
                      isLoading={false}
                      isEmpty={true}
                      chopHeightFromHundredPercentage={
                        _tableHeaderHeightRef?.current?.clientHeight
                      }
                    />
                  ),
                }}
                disableRowSelectionOnClick
                columnVisibilityModel={columnVisibilityModel}
                onColumnVisibilityModelChange={(newModel) =>
                  setColumnVisibilityModel(newModel)
                }
                loading={_QueryRateRead?.loading}
                disableColumnMenu={false}
                sx={{
                  "& .MuiDataGrid-main": {
                    border: "none",
                    borderRadius: 0,
                  },
                }}
              />
            </Flex>
          </Tabs>
        </Flex>
        <Modal
          size="md"
          title="Rate Create"
          isOpen={isRateCreateFormOpen}
          onClose={onRateCreateFormClose}
        >
          <RateUpsert isCreateOnly={true} type={_rates[tabIndex]} />
        </Modal>
        <Modal
          size="md"
          title="Rate Update"
          isOpen={isRateUpdateFormOpen}
          onClose={onRateUpdateFormClose}
        >
          <RateUpsert type={_rates[tabIndex]} />
        </Modal>
        <Modal
          size="md"
          title="Rate Delete"
          isOpen={isDeleteModalOpen}
          onClose={onDeleteModalClose}
        >
          <RateDelete />
        </Modal>
      </Flex>
    </>
  );
};

/*
 * PROPTYPES
 */
Index.propTypes = {};

/*
 * EXPORTS
 */
export default Index;
